import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Machine } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { t } from "../../../i18n/locale";

/**
 * Machines Page - View and manage production machines
 */
const Machines = () => {
  savePathToLocalStorage();

  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>("");
  // Non-host devices a machine can be linked to (Diosna, Escher, PLCs…).
  const [devices, setDevices] = useState<{ ID: number; Name: string }[]>([]);

  const emptyMachine: Partial<Machine> = {
    MachineName: "",
    MachineType: "Mikser",
    CapacityMaxKg: undefined,
    CapacityUnit: "kg_per_hour",
    Notes: "",
    IsActive: true,
    DeviceID: null,
  };

  const loadDevices = async () => {
    try {
      setDevices(await mesApi.getLinkableDevices());
    } catch (err: any) {
      console.error("Failed to load linkable devices:", err);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(true);
  const [machineForm, setMachineForm] = useState<Partial<Machine>>({ ...emptyMachine });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMachines();
  }, [filterType]);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadMachines = async () => {
    setLoading(true);
    try {
      const params: { machine_type?: string } = {};
      if (filterType) {
        params.machine_type = filterType;
      }
      const data = await mesApi.getMachines(params);
      setMachines(data);
    } catch (err: any) {
      toast.error("Failed to load machines: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMachine = () => {
    setMachineForm({ ...emptyMachine });
    setIsCreating(true);
    setModalOpen(true);
  };

  const handleEditMachine = (machine: Machine) => {
    setMachineForm({
      ID: machine.ID,
      MachineName: machine.MachineName,
      MachineType: machine.MachineType,
      CapacityMaxKg: machine.CapacityMaxKg,
      CapacityUnit: machine.CapacityUnit || "kg_per_hour",
      Notes: machine.Notes || "",
      IsActive: machine.IsActive ?? true,
      DeviceID: machine.DeviceID ?? null,
    });
    setIsCreating(false);
    setModalOpen(true);
  };

  const handleFieldChange = (field: keyof Machine, value: any) => {
    setMachineForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveMachine = async () => {
    if (!machineForm.MachineName || !machineForm.MachineName.trim()) {
      toast.error(t("Naziv stroja je obavezan"));
      return;
    }
    if (!machineForm.MachineType) {
      toast.error(t("Tip stroja je obavezan"));
      return;
    }

    setSaving(true);
    try {
      // Nulls (not undefined) so a cleared field survives JSON serialization and
      // actually clears the value on the backend.
      const payload = {
        MachineName: machineForm.MachineName.trim(),
        MachineType: machineForm.MachineType,
        CapacityMaxKg: machineForm.CapacityMaxKg ?? null,
        CapacityUnit: machineForm.CapacityUnit || "kg_per_hour",
        Notes: machineForm.Notes ?? null,
        IsActive: machineForm.IsActive ?? true,
        DeviceID: machineForm.DeviceID ?? null,
      } as unknown as Partial<Machine>;

      if (isCreating) {
        await mesApi.createMachine(payload);
        toast.success(t("Stroj uspjesno dodan"));
      } else {
        await mesApi.updateMachine(machineForm.ID as number, payload);
        toast.success(t("Stroj uspjesno azuriran"));
      }
      setModalOpen(false);
      loadMachines();
    } catch (err: any) {
      toast.error(
        t(isCreating ? "Greska pri dodavanju stroja: {message}" : "Greska pri azuriranju stroja: {message}", {
          message: err.message,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const getMachineTypeLabel = (type: string) => {
    switch (type) {
      // Canonical DB values are Croatian (Mikser/Obrada/Peć); keep a couple of
      // legacy English aliases so older rows still label correctly.
      case "Mikser": case "mixer": return t("Mikser");
      case "Obrada": case "processor": return t("Stroj za obradu");
      case "Peć": case "Pec": case "oven": return t("Peć");
      case "Komora": case "proofer": return t("Komora za dizanje");
      case "Ostalo": case "other": return t("Ostalo");
      default: return type;
    }
  };

  // Machine types for the filter/add dropdowns. Match the values actually stored
  // in the DB (Croatian) — the previous English list ("mixer"/"oven"/…) matched no
  // rows, so every machine was filtered out and the page showed "Nema strojeva".
  const KNOWN_TYPES = ["Mikser", "Obrada", "Peć", "Komora", "Ostalo"];
  // Include any type value present in the data but not in the known list, so no
  // machine is ever hidden by an unexpected type string.
  const machineTypes = Array.from(
    new Set([...KNOWN_TYPES, ...machines.map((m) => m.MachineType).filter(Boolean)])
  );

  // Group machines by type
  const machinesByType = machineTypes.reduce((acc, type) => {
    acc[type] = machines.filter((m) => m.MachineType === type);
    return acc;
  }, {} as Record<string, Machine[]>);

  const getMachineId = (m: Machine) => m.ID;

  return (
    <div className="mes-page machines-page">
      <Header title={t("MES - Strojevi")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Filter */}
          <div className="mes-section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                gap: "16px",
              }}
            >
              <div className="control-group" style={{ marginBottom: 0 }}>
                <label>{t("Tip stroja:")}</label>
                <select
                  className="select-classic"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">{t("Svi tipovi")}</option>
                  {machineTypes.map((type) => (
                    <option key={type} value={type}>
                      {getMachineTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn-submit" onClick={handleAddMachine}>
                {t("+ Dodaj stroj")}
              </button>
            </div>
          </div>

          {/* Machines Display */}
          {loading ? (
            <div className="mes-section loading-state">Loading...</div>
          ) : machines.length === 0 ? (
            <div className="mes-section empty-state">
              <p>{t("Nema strojeva u sustavu.")}</p>
            </div>
          ) : filterType ? (
            // Single type view
            <div className="mes-section">
              <h3>{getMachineTypeLabel(filterType)}</h3>
              <div className="machine-cards-grid">
                {machines.map((machine) => (
                  <MachineCard
                    key={getMachineId(machine)}
                    machine={machine}
                    getMachineTypeLabel={getMachineTypeLabel}
                    onEdit={handleEditMachine}
                  />
                ))}
              </div>
            </div>
          ) : (
            // All types grouped
            machineTypes.map((type) => {
              const typeMachines = machinesByType[type];
              if (typeMachines.length === 0) return null;
              return (
                <div key={type} className="mes-section">
                  <h3>{getMachineTypeLabel(type)}</h3>
                  <div className="machine-cards-grid">
                    {typeMachines.map((machine) => (
                      <MachineCard
                        key={getMachineId(machine)}
                        machine={machine}
                        getMachineTypeLabel={getMachineTypeLabel}
                        onEdit={handleEditMachine}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add / Edit Machine Modal */}
      {modalOpen && (
        <div className="mes-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isCreating ? t("Dodaj stroj") : t("Uredi stroj")}</h3>
              <button className="close-btn" onClick={() => setModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t("Naziv stroja *")}</label>
                <input
                  type="text"
                  value={machineForm.MachineName || ""}
                  onChange={(e) => handleFieldChange("MachineName", e.target.value)}
                  placeholder={t("npr. Mikser 1")}
                />
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label>{t("Tip stroja *")}</label>
                  <select
                    className="select-classic"
                    value={machineForm.MachineType || ""}
                    onChange={(e) => handleFieldChange("MachineType", e.target.value)}
                  >
                    {machineTypes.map((type) => (
                      <option key={type} value={type}>
                        {getMachineTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("Kapacitet (kg)")}</label>
                  <input
                    type="number"
                    value={machineForm.CapacityMaxKg ?? ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "CapacityMaxKg",
                        e.target.value === "" ? undefined : Number(e.target.value)
                      )
                    }
                    placeholder={t("npr. 100")}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t("Povezani uređaj (opcionalno)")}</label>
                <select
                  className="select-classic"
                  value={machineForm.DeviceID ?? ""}
                  onChange={(e) =>
                    handleFieldChange("DeviceID", e.target.value === "" ? null : Number(e.target.value))
                  }
                >
                  <option value="">{t("— nije povezano —")}</option>
                  {devices.map((d) => (
                    <option key={d.ID} value={d.ID}>{d.Name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t("Napomena")}</label>
                <textarea
                  value={machineForm.Notes || ""}
                  onChange={(e) => handleFieldChange("Notes", e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    checked={machineForm.IsActive ?? true}
                    onChange={(e) => handleFieldChange("IsActive", e.target.checked)}
                  />
                  <label>{t("Aktivan stroj")}</label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setModalOpen(false)}>
                {t("Odustani")}
              </button>
              <button className="btn-submit" onClick={handleSaveMachine} disabled={saving}>
                {saving ? t("Spremanje...") : isCreating ? t("Dodaj") : t("Spremi")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

// Machine Card Component
interface MachineCardProps {
  machine: Machine;
  getMachineTypeLabel: (type: string) => string;
  onEdit: (machine: Machine) => void;
}

const MachineCard: React.FC<MachineCardProps> = ({ machine, getMachineTypeLabel, onEdit }) => {
  const capacity = machine.CapacityMaxKg;

  return (
    <div className={`machine-card machine-type-${machine.MachineType}`}>
      <div className="machine-card-header">
        <div className="machine-info">
          <div className="machine-name">{machine.MachineName}</div>
        </div>
        <span className={`status-pill status-${machine.IsActive ? "active" : "inactive"}`}>
          <span className="status-dot"></span>
          {machine.IsActive ? t("Aktivan") : t("Neaktivan")}
        </span>
      </div>

      <div className="machine-card-body">
        <div className="machine-detail">
          <span className="detail-label">{t("Tip:")}</span>
          <span className={`detail-value machine-type-badge type-${machine.MachineType}`}>
            {getMachineTypeLabel(machine.MachineType)}
          </span>
        </div>

        {capacity && (
          <div className="machine-detail">
            <span className="detail-label">{t("Kapacitet:")}</span>
            <span className="detail-value">{capacity} kg</span>
          </div>
        )}

        <div className="machine-detail">
          <span className="detail-label">{t("Uređaj:")}</span>
          <span className="detail-value">
            {machine.Device ? (
              <>
                🔗 {machine.Device.Name}
                {machine.Device.Status === 1 ? " · online" : " · offline"}
              </>
            ) : (
              "—"
            )}
          </span>
        </div>
      </div>

      <div className="machine-card-actions">
        <button className="btn-secondary btn-sm" onClick={() => onEdit(machine)}>
          {t("Uredi")}
        </button>
      </div>
    </div>
  );
};

export default Machines;
